import {
  AppBar,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Container,
  createStyles,
  DialogActions,
  Grid,
  InputAdornment,
  List,
  ListItem,
  makeStyles,
  Theme,
  Toolbar,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { cloneDeep, get, isEqual } from "lodash";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useHistory, useParams } from "react-router-dom";

import SimpleTextField from "../../components/SimpleTextField";
import EGroup from "../../core/contracts/EGroup";
import useConstructor from "../../core/hooks/useConstructor";
import { useHubx, useHubXSubscribe } from "../../HubX";
import EditIcon from "@material-ui/icons/Edit";
import useAuth from "../../core/hooks/useAuth";
import NavigateIcon from "../../assets/icons/NavigateIcon";
import InfoIcon from "@material-ui/icons/Info";
import AddIcon from "@material-ui/icons/AddCircleOutline";
import { FETCH_GROUP, POST_GROUP } from "../../Fedx";
import MembersTable from "./MembersTable";
import SimpleDialog from "../../components/SimpleDialog";
import AddMemberDialog from "./AddMemberDialog";
import { isArrayValid } from "../../helpers/utility";
import clsx from "clsx";
import { EVENT_ADD_NEW_MEMBERS, EVENT_UPDATE_USER_DIRECTOR } from "../../Events";
import EUser from "../../core/contracts/EUser";
import { LocalCache } from "../../helpers/cache";
import NavigationPrompt from "react-router-navigation-prompt";
import DirectorManagerDialog from "./DirectorManagerDialog";
import EUserDirector from "../../core/contracts/EUserDirector";
import EUserDirectorEmail from "../../core/contracts/EUserDirectorEmail";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
    },

    appBar: {
      bottom: "0",
      top: "auto",
      background: theme.palette.background.paper,
    },

    errorText: {
      "color": theme.palette.error.main,

      "& svg": {
        color: theme.palette.error.main,
      },
    },

    deleteButton: {
      background: theme.palette.error.main,
      color: theme.palette.common.white,
    },

    saveButton: {
      background: theme.palette.success.main,
      color: theme.palette.common.white,
    },

    flexWrapper: {
      display: "flex",
      alignItems: "center",
    },

    flexSpacing: {
      columnGap: `${theme.spacing(2)}px`,
    },

    flexEnd: {
      justifyContent: "flex-end",
    },

    spaceBetween: {
      justifyContent: "space-between",
    },

    fullWidth: {
      flex: 1,
    },

    bold: {
      fontWeight: "bold",
    },

    displayOnlyValues: {
      display: "flex",
      justifyContent: "center",
      flexDirection: "column",
    },
  })
);

function EditGroup() {
  const hub = useHubx();
  const classes = useStyles();
  const toastr = useSnackbar();
  const routeParams = useParams();
  const history = useHistory();
  const auth = useAuth();
  const isNew = get(routeParams, "group_id") === "new";

  const [hasEdited, setHasEdited] = React.useState<boolean>(false);
  const [group, setGroup] = React.useState<EGroup | null>(null);
  const [editGroup, setEditGroup] = React.useState<null | EGroup>(() => {
    if (isNew) {
      const initialObj: EGroup = {
        id: "",
        nome: "",
        descricao: "",
        ver_pedidos: false,
        novo_pedido_fluxo6: false,
        novo_pedido_fluxo7_9: false,
        aprovacao_nivel1: false,
        aprovacao_nivel2: false,
        atualizacao_cadastral: false,
        usuarios: [],
      };

      return initialObj;
    }

    return null;
  });

  const [loading, setLoading] = React.useState<boolean>(true);
  const [openAddMemberDialog, setOpenAddMemberDialog] = React.useState<boolean>(false);
  const [openDirectorDialog, setOpenDirectorDialog] = React.useState<boolean>(false);
  const [userDirector, setUserDirector] = React.useState<EUserDirectorEmail>({
    email: "",
    diretorias: [],
  });
  const [selectedUsersCodes, setSelectedUsersCodes] = React.useState<string[]>([]);
  const [loadingSave, setLoadingSave] = React.useState<boolean>(false);

  const currentAdmin: EUser | undefined = React.useMemo(() => {
    const cache = new LocalCache();
    const userInfo: EUser | null = cache.get("user");

    if (userInfo) {
      return userInfo;
    }
  }, []);

  const anyChanges = !isEqual(group, editGroup);

  const fetchGroup = async (groupId: string) => {
    setLoading(true);
    try {
      const APIResponse: EGroup = await hub.request(FETCH_GROUP, groupId);
      setGroup(APIResponse);
      setEditGroup(cloneDeep(APIResponse));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("🚀 ~ file: index.tsx ~ line 31 ~ fetchGroup ~ error", error);

      toastr.enqueueSnackbar("Ops! Ocorreu um erro ao carregar as informações deste grupo!", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClickBack = () => {
    history.push("/groups/");
  };

  useConstructor(() => {
    const groupId = get(routeParams, "group_id");

    if (!isNew) {
      fetchGroup(groupId);
    }
  });

  const handleChange = (key: string, value: unknown) => {
    setEditGroup((prev) => {
      return { ...prev, [key]: value } as EGroup;
    });
  };

  const EditPermission = isNew || auth.isSuperAdmin;

  const showEditIcon = EditPermission ? (
    <InputAdornment position="end">
      <EditIcon />
    </InputAdornment>
  ) : null;

  const renderPermission = (key: string, label: string) => {
    const currentValue = get(editGroup, key, false);

    const handleChangeCheckBox = () => {
      handleChange(key, !currentValue);
    };

    return (
      <Grid item key={key} className={classes.flexWrapper}>
        <Checkbox color="secondary" checked={currentValue} onClick={handleChangeCheckBox} />

        <Typography>{label}</Typography>
      </Grid>
    );
  };

  const handleDeleteUser = (userCode: string) => {
    setEditGroup((prev) => {
      return {
        ...prev,
        usuarios: prev?.usuarios?.filter((x) => x.codigo !== userCode) ?? [],
      } as EGroup;
    });
  };

  const handleUserDirector = (userCode: string, diretorias?: EUserDirector[]) => {
    setUserDirector({
      diretorias: diretorias || [],
      email: userCode,
    });
    setOpenDirectorDialog(true);
  };

  const handleSelectUser = (userCode: string) => {
    if (selectedUsersCodes.includes(userCode)) {
      setSelectedUsersCodes((prev) => prev.filter((x) => x !== userCode));
    } else {
      setSelectedUsersCodes((prev) => [...prev, userCode]);
    }
  };

  const allUserCodes = React.useMemo(() => {
    return editGroup?.usuarios?.map((x) => x.codigo) ?? [];
  }, [editGroup?.usuarios]);

  const handleSelectAll = () => {
    if (isEqual(allUserCodes, selectedUsersCodes)) {
      setSelectedUsersCodes([]);
    } else {
      setSelectedUsersCodes(allUserCodes);
    }
  };

  const handleDeleteSelected = () => {
    setEditGroup((prev) => {
      return {
        ...prev,
        usuarios: prev?.usuarios?.filter((x) => !selectedUsersCodes.includes(x.codigo)) ?? [],
      } as EGroup;
    });

    setSelectedUsersCodes([]);
  };

  const handleSave = async () => {
    setLoadingSave(true);
    try {
      const APIResponse = await hub.request(POST_GROUP, {
        newGroup: editGroup,
        previousGroup: group,
      });

      if (APIResponse) {
        toastr.enqueueSnackbar("Grupo salvo com sucesso!", { variant: "success" });
        setHasEdited(true);
        history.push("/groups/");
      } else {
        toastr.enqueueSnackbar("Ops! Não foi possível salvar as mudanças", { variant: "warning" });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      toastr.enqueueSnackbar("Ops! Ocorreu um erro ao salvar as informações do grupo!", {
        variant: "error",
      });
    } finally {
      setLoadingSave(false);
    }
  };

  const formError = !anyChanges || !editGroup?.nome; /* || !editGroup?.descricao*/

  const getErrorDescription = () => {
    const reasons = [];

    if (!anyChanges) {
      return "Nenhuma alteração para salvar";
    }

    if (!editGroup?.nome) {
      reasons.push("Forneça um nome ao grupo");
    }

    // if (!editGroup?.descricao) {
    //   reasons.push("Forneça uma descrição ao grupo");
    // }

    if (isArrayValid(reasons)) {
      return (
        <List>
          {reasons.map((x, idx) => (
            <ListItem key={`reason_${idx}`}>{x}</ListItem>
          ))}
        </List>
      );
    }

    return "";
  };

  const addNewMember = (newUsers: EUser[]) => {
    setEditGroup((prev) => {
      return { ...prev, usuarios: [...(prev?.usuarios ?? []), ...newUsers] } as EGroup;
    });
  };

  const canEditAdminGroup =
    group?.id === "B3726236-C10A-4DC4-B524-DA2571E2B104".toLowerCase() &&
    !currentAdmin?.super_usuario;

  useHubXSubscribe(EVENT_ADD_NEW_MEMBERS, addNewMember);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateUserDirectors = (updateDirectors: any) => {
    setLoading(true);
    let userUpdate: EUser | undefined = editGroup?.usuarios.find(
      (u) => u.email === updateDirectors.email
    );

    if (userUpdate) {
      userUpdate.usuarios_diretorias = updateDirectors.directors;
    }

    userUpdate = group?.usuarios.find((u) => u.email === updateDirectors.email);

    if (userUpdate) {
      userUpdate.usuarios_diretorias = updateDirectors.directors;
    }
    setLoading(false);
  };

  useHubXSubscribe(EVENT_UPDATE_USER_DIRECTOR, updateUserDirectors);

  const renderValue = (value?: string | number | null) => {
    return value || value === 0 ? value : "-";
  };

  return (
    <>
      <Container maxWidth={"xl"}>
        <NavigationPrompt when={!formError && !hasEdited}>
          {({ onConfirm, onCancel }) => {
            return (
              <SimpleDialog
                open={true}
                handleClose={() => onCancel()}
                handleConfirm={() => onConfirm()}
                title="Atenção"
                footer={true}
              >
                <>
                  <span>
                    <b>Suas alterações ainda não foram salvas.</b> Caso você saia da tela, elas
                    serão descartadas.
                  </span>
                  <br></br>
                  <br></br>
                  <span>Deseja confirmar essa ação?</span>
                </>
              </SimpleDialog>
            );
          }}
        </NavigationPrompt>
        <div>
          <Tooltip title="Voltar para página de listagem de grupos">
            <Button onClick={handleClickBack}>
              <NavigateIcon transform="rotate(-180)" /> <strong>Voltar</strong>
            </Button>
          </Tooltip>
        </div>
        <Card className={classes.root}>
          <Grid container direction="column" spacing={2}>
            {/* Title */}
            <Grid item>
              <div className={clsx(classes.flexWrapper, classes.spaceBetween)}>
                <Typography variant="h5" className={classes.bold}>
                  Editar Grupo
                </Typography>

                {!loadingSave && (
                  <Tooltip title={formError ? getErrorDescription() : ""}>
                    <span>
                      <Button
                        className={classes.saveButton}
                        variant="contained"
                        disabled={formError}
                        onClick={handleSave}
                      >
                        Salvar
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {loadingSave && <CircularProgress color="primary" />}
              </div>
            </Grid>

            {/* Edit Name & Description */}
            <Grid item container spacing={2} direction="column">
              <Grid item>
                <Typography className={classes.bold}>Dados Gerais</Typography>
              </Grid>

              <Grid container item spacing={3}>
                <Grid item xs={4}>
                  {EditPermission ? (
                    <SimpleTextField
                      autoComplete="off"
                      value={editGroup?.nome ?? ""}
                      onChange={(e) => handleChange("nome", e?.target?.value ?? "")}
                      label="Nome"
                      id="editGroupName"
                      disabled={!EditPermission}
                      InputProps={{
                        endAdornment: showEditIcon,
                      }}
                    />
                  ) : (
                    <div className={classes.displayOnlyValues}>
                      <Typography className={classes.bold}>Nome</Typography>
                      <div>{renderValue(group?.nome)}</div>
                    </div>
                  )}
                </Grid>

                <Grid item xs={4}>
                  {EditPermission ? (
                    <SimpleTextField
                      autoComplete="off"
                      value={editGroup?.descricao ?? ""}
                      onChange={(e) => handleChange("descricao", e?.target?.value ?? "")}
                      label="Descrição"
                      id="editGroupdescription"
                      disabled={!EditPermission}
                      InputProps={{
                        endAdornment: showEditIcon,
                      }}
                    />
                  ) : (
                    <div className={classes.displayOnlyValues}>
                      <Typography className={classes.bold}>Descrição</Typography>
                      <div>{renderValue(group?.descricao)}</div>
                    </div>
                  )}
                </Grid>
              </Grid>

              <Grid item>
                {group?.tem_restricao_cliente && (
                  <div className={clsx(classes.flexWrapper, classes.errorText)}>
                    <InfoIcon />

                    <Typography>
                      Os membros deste grupo verão apenas eventos dos clientes associados a eles.
                    </Typography>
                  </div>
                )}
              </Grid>
            </Grid>

            {/* Permissions */}
            <Grid item>
              <Typography className={classes.bold}>Permissões</Typography>

              <Grid container spacing={2}>
                {renderPermission("ver_pedidos", "Ver Pedidos")}
                {renderPermission("aprovacao_nivel1", "Aprovação para emissão")}
                {renderPermission("aprovacao_nivel2", "Aprovação de não emissão 2 nível")}
                {renderPermission("novo_pedido_fluxo6", "Entrar com novo pedido (fluxo 6)")}
                {renderPermission("novo_pedido_fluxo7_9", "Entrar com novo pedido (fluxo 7)")}
                {/* {renderPermission("atualizacao_cadastral", "Atualização cadastral")} */}
              </Grid>
            </Grid>

            {/* Membros */}
            <Grid item>
              <Toolbar>
                <Grid
                  className={clsx(classes.flexWrapper, classes.flexEnd, classes.fullWidth)}
                  justify="flex-end"
                  spacing={3}
                >
                  <Button
                    variant="text"
                    disabled={canEditAdminGroup}
                    onClick={() => setOpenAddMemberDialog(true)}
                  >
                    <AddIcon />
                    <strong>Novo Membro</strong>
                  </Button>
                </Grid>
              </Toolbar>

              <MembersTable
                loading={loading}
                rows={editGroup?.usuarios ?? []}
                selectedRows={selectedUsersCodes}
                handleDelete={handleDeleteUser}
                handleSelect={handleSelectUser}
                handleDirector={handleUserDirector}
                handleSelectAll={handleSelectAll}
                group={group}
                currentAdmin={currentAdmin}
              />
            </Grid>
          </Grid>
        </Card>
      </Container>

      {isArrayValid(selectedUsersCodes) && (
        <AppBar position="fixed" className={classes.appBar} component="footer">
          <Container>
            <div className={clsx(classes.flexWrapper, classes.spaceBetween)}>
              <strong>{`${selectedUsersCodes?.length} membros selecionados`}</strong>

              <DialogActions>
                <Button variant="outlined" onClick={() => setSelectedUsersCodes([])}>
                  Cancelar
                </Button>
                <Button
                  className={classes.deleteButton}
                  variant="contained"
                  onClick={handleDeleteSelected}
                >
                  Excluir
                </Button>
              </DialogActions>
            </div>
          </Container>
        </AppBar>
      )}

      <SimpleDialog
        open={openAddMemberDialog}
        handleClose={() => setOpenAddMemberDialog(false)}
        title="Adicionar Membro"
      >
        <AddMemberDialog handleClickCancel={() => setOpenAddMemberDialog(false)} />
      </SimpleDialog>

      <SimpleDialog
        open={openDirectorDialog}
        handleClose={() => setOpenDirectorDialog(false)}
        title="Gerenciar Diretorias"
      >
        <DirectorManagerDialog
          handleClickCancel={() => setOpenDirectorDialog(false)}
          userDirectors={userDirector}
        />
      </SimpleDialog>
    </>
  );
}

export default EditGroup;
